import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { REST_API_URL } from 'env';
import { BOOKING_FORGOT_PASSWORD_PATH, BOOKING_HOME_PATH, BOOKING_REGISTRATION_PATH } from 'routes/paths';
import logoSvg from 'assets/logo.svg';
import useLang from 'lang';
import 'pages/booking/BookingLogin.css';
import 'pages/booking/booking.css';
import { useApi } from 'api/api-context';

/**
 * Login page for the booking section — authenticates via email/password
 * and stores the resulting JWT by redirecting to the booking home with ?jwt=.
 */
export default function BookingLogin() {
  const { logoUrl } = useApi();
  const { _t } = useLang();
  const [searchParams] = useSearchParams();
  const registrationSlug = searchParams.get('slug');
  const registrationPath = registrationSlug ? BOOKING_REGISTRATION_PATH.insert({ slug: registrationSlug }) : null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [showTotp, setShowTotp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Submits credentials and redirects with the JWT on success. */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data } = await axios.post(`${REST_API_URL}/login`, {
        email,
        password,
        platform: 'booking',
        ...(totp ? { totp } : {}),
      });

      const token: string = data.token;
      const firstBuildingId: number | undefined = data.buildings?.[0]?.id;

      const params = new URLSearchParams({ jwt: token });
      if (firstBuildingId) params.set('buildingId', String(firstBuildingId));

      window.location.href = `${BOOKING_HOME_PATH.original}?${params.toString()}`;
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.msg;
      const code = err.response?.data?.code;

      if (status === 401 && (msg?.toLowerCase().includes('totp') || code === 4313)) {
        if (!showTotp) {
          setShowTotp(true);
        } else {
          setError(_t('TOTP code is invalid. Please try again.'));
        }
      } else if (status === 401) {
        setError(_t('Incorrect email or password.'));
      } else {
        setError(msg || _t('Login failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="booking-root login-page">
      <div className="login-card">
        <div className="login-logo">
          <img src={logoUrl ?? logoSvg} alt={_t('Logo')} />
        </div>
        <h1 className="login-title">{_t('Login')}</h1>
        <p className="login-subtitle">{_t('Log in to access the booking system.')}</p>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="booking-login-email">
              {_t('Email')}
            </label>
            <input
              id="booking-login-email"
              className="form-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={_t('your@email.com')}
              required
              autoComplete="email"
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="booking-login-password">
              {_t('Password')}
            </label>
            <input
              id="booking-login-password"
              className="form-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>
          {showTotp && (
            <div className="form-group">
              <label className="form-label" htmlFor="booking-login-totp">
                {_t('TOTP code (2FA)')}
              </label>
              <input
                id="booking-login-totp"
                className="form-input"
                type="text"
                value={totp}
                onChange={(e) => setTotp(e.target.value)}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
              />
            </div>
          )}
          {error && <p className="login-error">{error}</p>}
          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? _t('Logging in...') : _t('Log in')}
          </button>
        </form>
        <Link className="login-link" to={BOOKING_FORGOT_PASSWORD_PATH.original}>
          {_t('Forgot your password?')}
        </Link>
        {registrationPath && (
          <div className="login-register-cta">
            <p className="login-register-prompt">{_t("Don't have an account yet?")}</p>
            <Link className="login-register-link" to={registrationPath}>
              {_t('Register')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
